import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import styles from "../reminder.module.css";
import MTopBar from "@/components/ui/atoms/MTopBar";
import MHeader from "@/components/ui/organisms/MHeader";
import headerStyles from "@/components/ui/organisms/MHeader.module.css";
import { getMemberInfo } from "@/hooks/useMembers";
import MInputField from "@/components/ui/atoms/MInputField";
import { MButton } from "@/components/ui/atoms/MButtons";
import { useReminders } from "@/hooks/useReminders";
import { CreateReminder, Reminders, ReminderType } from "@/types/reminders";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import { getPossessiveName, paintEventDateField } from "../remindersHelper";
import ReminderSummaryScreen, {
  ReminderSummaryTimeSlot,
} from "../ReminderSummaryScreen";
import {
  formatPrettyDate,
  getTimezone,
  initDate,
  inputFieldTime,
  updateTime,
} from "@/utils/date-utils";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { goBackOneLevel } from "@/utils/nav-utils";
import { useInsurance } from "@/hooks/useInsurance";
import { MAccordionHeader } from "@/components/ui/atoms/MAccordionHeader";
import MEmptyCard from "@/components/ui/organisms/MEmptyCard";
import { Insurance } from "@/types/insurance";
import { useTypeAhead } from "@/hooks/useTypeAhead";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { isPast } from "date-fns";
import {
  IconBoltFilled,
  IconFileOff,
  IconPlus,
  IconShieldHeart,
} from "@tabler/icons-react";
import { trackUserDataLoaded } from "@/utils/gtm";
import {
  ANALYTICS_SCREEN_TYPES,
  ANALYTICS_SCREEN_NAMES,
} from "@/constants/analytics.constants";

enum InsuranceStage {
  START,
  DETAILS,
  ADD,
}

/**
 * Insurance Reminder Creation Page
 * Allows users to select an insurance type to create a reminder for
 */
const InsuranceReminder: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const reminder: Reminders = location.state?.reminder;
  const isEditing = !!reminder;
  const memberInfo = getMemberInfo();
  const defaultPrimaryHolder =
    memberInfo.nickname?.trim() || memberInfo.firstName?.trim() || null;

  const [selectedInsurance, setSelectedInsurance] = useState<string | null>(
    reminder?.reminderFor ?? null
  );
  const [isCritical] = useState<boolean>(
    reminder ? reminder.tier === "CRITICAL" : false
  );
  const { values: insurancePvtOptions } = useTypeAhead("insurance_private");
  const { values: insuranceGovOptions } = useTypeAhead("insurance_govt");

  // Figma shows this section already expanded on first load, not collapsed
  // behind a tap (5257-62245/62213).
  const [showInsuranceAccordion, setShowInsuranceAccordion] =
    useState<boolean>(true);
  const [stage, setStage] = useState<InsuranceStage>(
    isEditing ? InsuranceStage.ADD : InsuranceStage.START
  );
  const [expiryDate, setExpiryDate] = useState<Date | null>(
    initDate(reminder?.eventDate)
  );
  const [policyNumber, setPolicyNumber] = useState<string | null>(
    reminder?.reminderAttrs?.policyNumber ?? null
  );
  const [reminderDate, setReminderDate] = useState<Date | null>(
    initDate(reminder?.startDate)
  );
  const [reminderTime, setReminderTime] = useState<string | null>(
    inputFieldTime(reminder?.startDate, undefined, false, reminder?.timezone) ||
      null
  );
  const [notes, setNotes] = useState<string | null>(reminder?.notes ?? null);
  const { addReminder, updateReminder, totalCount, fetchReminders } =
    useReminders(memberInfo.id);

  // Fetch reminder count on mount
  useEffect(() => {
    fetchReminders();
  }, []);
  const { fetched, insurances, fetchInsurance } = useInsurance({ memberInfo });
  const [isExistingInsurance, setIsExistingInsurance] =
    useState<boolean>(false);
  // Primary policy holder is now always asked (Figma 5257-62182/62197) —
  // defaults to the member's own name in manual mode, editable; prefilled
  // and read-only once an existing saved policy is picked.
  const [primaryHolder, setPrimaryHolder] = useState<string | null>(
    reminder?.reminderAttrs?.primaryHolder ?? defaultPrimaryHolder
  );
  const [filteredInsuranceOptions, setFilteredInsuranceOptions] = useState<
    { name: string }[]
  >([]);

  const memberName = getPossessiveName(defaultPrimaryHolder);

  // Track page load once
  useEffect(() => {
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE,
    });
  }, []);

  useEffect(() => {
    if (!fetched) {
      fetchInsurance(true);
    }
  }, [fetched, fetchInsurance]);

  // Initialize filtered insurance options when typeahead data changes
  useEffect(() => {
    const allOptions = [
      ...insurancePvtOptions.map((name) => ({ name })),
      ...insuranceGovOptions.map((name) => ({ name })),
    ];
    setFilteredInsuranceOptions(allOptions);
  }, [insurancePvtOptions, insuranceGovOptions]);

  const getInsuranceDisplayName = (insurance: Insurance) => {
    return insurance.insurerName || insurance.schemeName;
  };

  const handleInsuranceSelect = (insurance: Insurance) => {
    if (!insurance) return;
    setSelectedInsurance(getInsuranceDisplayName(insurance));
    setPolicyNumber(insurance.policyNumber ?? null);
    if (insurance.policyEndDate && !isPast(insurance.policyEndDate)) {
      setExpiryDate(new Date(insurance.policyEndDate));
    } else {
      setExpiryDate(null);
    }
    setIsExistingInsurance(true);
    setPrimaryHolder(insurance.primaryPolicyHolderName);
  };

  // Typing/selecting a custom provider name clears whatever an earlier
  // saved-policy selection left behind — otherwise switching from a picked
  // policy back to manual entry keeps showing that policy's (now stale)
  // holder/number. This fires on every keystroke (it's the provider-name
  // field's onChange), so the actual clearing only happens once, right when
  // isExistingInsurance is still true from a prior pick — continuing to
  // type/edit an already-manual entry must not keep wiping primaryHolder/
  // expiryDate the user has since filled in on the DETAILS stage.
  const resetToManualEntry = (value: string) => {
    setSelectedInsurance(value);
    if (isExistingInsurance) {
      setIsExistingInsurance(false);
      setPolicyNumber(null);
      setPrimaryHolder(defaultPrimaryHolder);
      setExpiryDate(null);
    }
  };

  const handleBack = () => {
    if (isEditing) {
      navigate(goBackOneLevel(location.pathname));
      return;
    }
    const nextStageVal = stage - 1;
    if (nextStageVal < InsuranceStage.START) {
      navigate(-1);
    } else {
      setStage(nextStageVal);
    }
  };

  const isDetailsInvalid = (): boolean =>
    !selectedInsurance || !expiryDate || !primaryHolder;

  const nextStage = () => {
    const nextStageVal = stage + 1;
    if (nextStageVal > InsuranceStage.ADD) {
      showToast({
        message: t("errors.maxStageReached"),
        variant: "error",
      });
      return;
    }
    if (stage === InsuranceStage.DETAILS && isDetailsInvalid()) return;
    if (stage === InsuranceStage.START && !selectedInsurance) return;

    setStage(nextStageVal);

    if (nextStageVal === InsuranceStage.DETAILS) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE_INFORMATION,
      });
    } else if (nextStageVal === InsuranceStage.ADD) {
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
        screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE_SUMMARY,
      });
    }

    setTimeout(() => {
      document.querySelector("main")?.scrollTo({ top: 0, behavior: "smooth" });
    }, 100);
  };

  const paintProviderInput = () => {
    const handleSearch = (query: string) => {
      const allInsuranceOptions = [
        ...insurancePvtOptions.map((name) => ({ name })),
        ...insuranceGovOptions.map((name) => ({ name })),
      ];

      if (!query || query.trim() === "") {
        setFilteredInsuranceOptions(allInsuranceOptions);
      } else {
        const filtered = allInsuranceOptions.filter((option) =>
          option.name.toLowerCase().includes(query.toLowerCase())
        );
        setFilteredInsuranceOptions(filtered);
      }
    };

    return (
      <AutoCompleteInput<{ name: string }>
        label={t("reminders.insurance.reminderForLabel")}
        data={filteredInsuranceOptions}
        getOptionLabel={(option) => option.name}
        onSelect={(item) => {
          const value =
            typeof item === "string"
              ? item
              : item && typeof item === "object" && "name" in item
              ? item.name
              : "";
          resetToManualEntry(value);
        }}
        placeholder={t("common.notAnswered")}
        value={selectedInsurance || ""}
        onChange={(v) => resetToManualEntry(v)}
        onSearch={handleSearch}
        allowCustomInput={true}
        showAllOnFocus={true}
        className="w-full"
      />
    );
  };

  // Figma 5257-62245/62213: the "saved policies" accordion is always shown
  // (empty-state card included), not hidden away when there's nothing
  // saved yet — matches medication's picker treatment of the same idea.
  const paintInsuranceSelect = () => {
    return (
      <div className={styles.optionsContainer}>
        <div className="flex items-center gap-3">
          <div className={styles.policyAccordionIcon}>
            <IconBoltFilled size={24} color="#8764BB" />
          </div>
          <div className="flex-1">
            <MAccordionHeader
              title={t("reminders.insurance.selectFromList")}
              isOpen={showInsuranceAccordion}
              onClick={() => setShowInsuranceAccordion(!showInsuranceAccordion)}
              className="accordion-header-16"
              titleColor="var(--brand-colours-indigo-indigo-700, #45168C)"
            />
          </div>
        </div>
        {showInsuranceAccordion &&
          (insurances.length === 0 ? (
            <div className="mt-4">
              <MEmptyCard
                icon={<IconFileOff size={64} stroke={2} color="#AD95D1" />}
                primaryTitle={t("reminders.insurance.noPolicySaved")}
                secondaryTitle={t(
                  "reminders.insurance.noPolicySavedDescription"
                )}
                primaryCTA={{
                  text: t("reminders.insurance.addPolicyCta"),
                  leftIcon: <IconPlus size={16} />,
                  onClick: () =>
                    navigate("/profile/basic-information/insurance/add"),
                }}
              />
            </div>
          ) : (
            <div className={`${styles.optionsContainer} mt-4`}>
              {insurances.map((insurance) => {
                const displayName = getInsuranceDisplayName(insurance);
                const holder = insurance.primaryPolicyHolderName;
                const validUntil = insurance.isLifetimePolicy
                  ? t("insurance.lifetime")
                  : insurance.policyEndDate
                  ? formatPrettyDate(new Date(insurance.policyEndDate))
                  : null;
                const isSelected =
                  isExistingInsurance && selectedInsurance === displayName;

                return (
                  <button
                    key={insurance._id || insurance.id}
                    type="button"
                    className={`${styles.policyCard} ${
                      isSelected ? styles.policyCardSelected : ""
                    }`}
                    onClick={() => handleInsuranceSelect(insurance)}
                  >
                    <span className={styles.policyCardName}>{displayName}</span>
                    {holder && (
                      <span className={styles.policyCardMeta}>
                        Primary holder: {holder}
                      </span>
                    )}
                    {validUntil && (
                      <span
                        className={`${styles.policyCardMeta} ${styles.policyCardMetaBold}`}
                      >
                        Valid until: {validUntil}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}
      </div>
    );
  };

  const paintPolicyNumberInput = () => {
    // Figma 5257-62182 (manual entry) has no policy-number field at all —
    // it only appears once an existing saved policy fills it in (5257-62197).
    if (!isExistingInsurance) return null;
    return (
      <MInputField
        label={t("reminders.insurance.policyNumberLabel")}
        placeholder={t("common.notAnswered")}
        value={policyNumber}
        onChange={(e) => setPolicyNumber(e.target.value)}
        disabled={true}
        variant="Underlined"
      />
    );
  };

  const paintPolicyPrimaryHolder = () => {
    return (
      <MInputField
        label={t("reminders.insurance.primaryHolderLabel")}
        placeholder={t("common.notAnswered")}
        value={primaryHolder}
        onChange={(e) => setPrimaryHolder(e.target.value)}
        disabled={isExistingInsurance}
        variant="Underlined"
      />
    );
  };

  // pass a function to check for disable state
  const paintNextButton = (disableCheck: () => boolean) => {
    return (
      <MButton
        variant="filled"
        size="lg"
        onClick={() => nextStage()}
        className={styles.nextButton}
        disabled={disableCheck()}
      >
        {t("common.next")}
      </MButton>
    );
  };

  const paintHeader = (
    title: string,
    description?: string,
    addSpacer: boolean = true
  ) => {
    return (
      <>
        <MHeader
          module="reminders"
          memberInfo={memberInfo}
          title={title}
          description={description}
          useParentGradient={false}
          className={
            isFinalStage
              ? headerStyles.stickyBelowTopBarWithGradient
              : headerStyles.stickyBelowTopBar
          }
        />
        {addSpacer && <MSpacer />}
      </>
    );
  };

  const getScreenTitle = () =>
    `${
      isEditing ? t("reminders.insurance.editTitlePrefix") : ""
    }${memberName} ${t("reminders.insurance.detailsTitleSuffix")}`;

  const dosageTimeSlots: ReminderSummaryTimeSlot[] = [
    {
      label: "",
      value: reminderTime,
      onChange: setReminderTime,
      showBadge: false,
    },
  ];

  const isAddInvalid = (): boolean =>
    !selectedInsurance ||
    !expiryDate ||
    !primaryHolder ||
    !reminderDate ||
    !reminderTime;

  const handleCreateReminder = async () => {
    // Check if this is first reminder BEFORE adding
    const isFirstReminder = totalCount === 0;

    const timezone = getTimezone();
    const newReminder: CreateReminder = {
      type: ReminderType.INSURANCE,
      startDate: updateTime(reminderDate, reminderTime),
      endDate: updateTime(reminderDate, reminderTime),
      reminderFor: selectedInsurance,
      eventDate: expiryDate,
      notes,
      reminderAttrs: {
        policyNumber,
        primaryHolder,
      },
      timezone,
      tier: isCritical ? "CRITICAL" : "ROUTINE",
    };

    if (isEditing) {
      await updateReminder(reminder.id, newReminder);
    } else {
      await addReminder(newReminder);
    }

    // Track reminder completion
    trackUserDataLoaded({
      memberInfo,
      screenType: ANALYTICS_SCREEN_TYPES.REMINDER,
      screenName: ANALYTICS_SCREEN_NAMES.REMINDER_ADD_INSURANCE_COMPLETE,
      ...(isFirstReminder && !isEditing && { action: "first_reminder_added" }),
    });

    navigate("/reminders");
  };

  const isFinalStage = stage === InsuranceStage.ADD;

  return (
    <div
      className={`flex flex-col min-h-screen relative pb-[24px] ${
        isFinalStage ? styles.addStageBackgroundFinal : ""
      }`}
    >
      {/* Sticky wrapper so the topbar scrolls together with the sticky
          MHeader below it instead of scrolling away on its own. */}
      <div className="sticky top-0 z-[31]">
        <MTopBar
          hideProfilePicker
          exitPath="/reminders"
          onBack={handleBack}
          withBackground={!isFinalStage}
          showPurpleGradient={isFinalStage}
        />
      </div>

      <div
        className="profile-page-root flex flex-col"
        style={{ minHeight: "calc(var(--vh, 1vh) * 100 - 128px)" }}
      >
        <div className={styles.content}>
          {stage === InsuranceStage.START && (
            <div className={styles.cardContainer}>
              <div className="relative w-full h-full flex flex-col">
                {paintHeader(
                  getScreenTitle(),
                  t("reminders.insurance.startDescription")
                )}
                <div className="profile-input-container flex-1">
                  {paintProviderInput()}
                  {paintInsuranceSelect()}
                </div>
                <div className="confirm-footer pt-0">
                  {paintNextButton(() => !selectedInsurance)}
                </div>
              </div>
            </div>
          )}
          {stage === InsuranceStage.DETAILS && (
            <div className="relative w-full h-full flex flex-col">
              {paintHeader(
                getScreenTitle(),
                t("reminders.insurance.detailsPromptDescription")
              )}
              <div className="profile-input-container flex-1">
                {paintPolicyPrimaryHolder()}
                {paintEventDateField(expiryDate, setExpiryDate, t, "insurance")}
                {paintPolicyNumberInput()}
              </div>
              <div className="confirm-footer pt-0">
                {paintNextButton(isDetailsInvalid)}
              </div>
            </div>
          )}
          {stage === InsuranceStage.ADD && (
            <ReminderSummaryScreen
              memberInfo={memberInfo}
              title={getScreenTitle()}
              description={t(
                isEditing
                  ? "reminders.insurance.editPrompt"
                  : "reminders.appointment.summaryScreenDescription"
              )}
              labelKey="insurance"
              recapIcon={<IconShieldHeart size={24} color="#8764BB" />}
              recapIconClass={styles.typeIconFlatInsurance}
              recapLabel={selectedInsurance || ""}
              recapSecondaryText={primaryHolder || ""}
              recapSecondaryIcon={null}
              recapSecondaryTextClassName={styles.appointmentRecapCaption}
              recapTertiaryText={
                expiryDate
                  ? t("reminders.insurance.validUntilRecapLabel", {
                      date: formatPrettyDate(expiryDate),
                    })
                  : ""
              }
              reminderDate={reminderDate}
              setReminderDate={setReminderDate}
              showEndDate={false}
              reminderEndDate={null}
              setReminderEndDate={() => {}}
              timeSlots={dosageTimeSlots}
              notes={notes}
              setNotes={setNotes}
              isInvalid={isAddInvalid()}
              onSubmit={handleCreateReminder}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default InsuranceReminder;
